import React, { useEffect, useSyncExternalStore } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  getPageSeoSnapshot,
  loadPageSeo,
  subscribePageSeo,
} from "../lib/seoStore";

const SITE_URL = "https://dotconverse.com";
const SITE_NAME = "dotConverse";
const DEFAULT_IMG = "/og-image.png";

function useDbSeo(path) {
  // Fire the fetch once per path; subsequent renders are read-only.
  if (path) loadPageSeo(path);
  const snapshot = useSyncExternalStore(
    subscribePageSeo,
    () => getPageSeoSnapshot(path),
    () => getPageSeoSnapshot(path),
  );
  return snapshot;
}

/**
 * Per-page SEO component. Merges page-level props with admin-managed DB
 * overrides (page_seo collection, keyed by URL pathname). DB values win
 * over props which win over hardcoded defaults.
 */
export default function Seo({
  title,
  description,
  path,
  image = DEFAULT_IMG,
  type = "website",
  structuredData,
  keywords,
  noIndex = false,
}) {
  const loc = useLocation();
  const resolvedPath = path || loc.pathname || "/";
  const db = useDbSeo(resolvedPath);

  // Title
  const propTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} — B2B Marketing, Powered by AI & Automation`;
  const fullTitle = db.seo_title || propTitle;

  // Description
  const finalDesc = db.seo_description || description || "";

  // Keywords
  const finalKeywords = db.seo_keywords || keywords || "";

  // Canonical
  const canonical = db.canonical_url || `${SITE_URL}${resolvedPath}`;

  // Image (OG + Twitter)
  const fallbackImg = image.startsWith("http") ? image : `${SITE_URL}${image}`;
  const ogImage = db.og_image || fallbackImg;

  // OG title + description (can be overridden separately from <title>)
  const ogTitle = db.og_title || fullTitle;
  const ogDescription = db.og_description || finalDesc;

  // Twitter card
  const twitterCard = db.twitter_card || "summary_large_image";

  // Robots
  let robots = db.robots;
  if (!robots) robots = noIndex ? "noindex,nofollow" : "index,follow";

  // JSON-LD: DB override (raw string) or component prop (object)
  let jsonld = null;
  if (db.schema_jsonld) {
    try {
      jsonld = JSON.parse(db.schema_jsonld);
    } catch {
      jsonld = null;
    }
  } else if (structuredData) {
    jsonld = structuredData;
  }

  // Safety-net direct-DOM sync. react-helmet-async@3 has documented edge
  // cases where Helmet doesn't reflect updates when the underlying state
  // changes via an external store (useSyncExternalStore + lazy fetch).
  // Setting the head tags directly here guarantees the browser tab title
  // and core meta tags reflect CMS overrides the moment the fetch resolves.
  useEffect(() => {
    if (!fullTitle) return;
    if (document.title !== fullTitle) document.title = fullTitle;

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (value == null || value === "") {
        if (el) el.setAttribute(attr, "");
        return;
      }
      if (!el) {
        // Create the tag if missing. Pull the selector apart for either
        // `meta[name="..."]` or `meta[property="..."]` or `link[rel="..."]`.
        const tagMatch = selector.match(/^([a-z]+)\[([a-z-]+)="([^"]+)"\]$/);
        if (!tagMatch) return;
        el = document.createElement(tagMatch[1]);
        el.setAttribute(tagMatch[2], tagMatch[3]);
        document.head.appendChild(el);
      }
      if (el.getAttribute(attr) !== value) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', "content", finalDesc);
    setMeta('meta[name="keywords"]', "content", finalKeywords);
    setMeta('link[rel="canonical"]', "href", canonical);
    setMeta('meta[name="robots"]', "content", robots);
    setMeta('meta[property="og:title"]', "content", ogTitle);
    setMeta('meta[property="og:description"]', "content", ogDescription);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[property="og:image"]', "content", ogImage);
    setMeta('meta[property="og:type"]', "content", type);
    setMeta('meta[name="twitter:card"]', "content", twitterCard);
    setMeta('meta[name="twitter:title"]', "content", ogTitle);
    setMeta('meta[name="twitter:description"]', "content", ogDescription);
    setMeta('meta[name="twitter:image"]', "content", ogImage);
  }, [
    fullTitle,
    finalDesc,
    finalKeywords,
    canonical,
    robots,
    ogTitle,
    ogDescription,
    ogImage,
    type,
    twitterCard,
  ]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {finalDesc && <meta name="description" content={finalDesc} />}
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={robots} />

      {/* OpenGraph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={ogTitle} />
      {ogDescription && <meta property="og:description" content={ogDescription} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={ogTitle} />
      {ogDescription && <meta name="twitter:description" content={ogDescription} />}
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {jsonld && (
        <script type="application/ld+json">{JSON.stringify(jsonld)}</script>
      )}
    </Helmet>
  );
}

export const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "dotConverse",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description:
    "dotConverse is a B2B digital & MarTech agency helping Fortune 1000s and high-growth brands engineer full-funnel, AI-led marketing pipelines.",
  foundingDate: "2013",
  founder: {
    "@type": "Person",
    name: "Saurabh Pandey",
    jobTitle: "Founder & CEO",
  },
  sameAs: [
    "https://www.linkedin.com/company/dotconverse",
    "https://twitter.com/dotconverse",
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "2-C, Floor LG, Tower-4, RPS 12th Avenue, Mathura Road",
    addressLocality: "Faridabad",
    addressRegion: "Haryana",
    postalCode: "121001",
    addressCountry: "IN",
  },
  contactPoint: {
    "@type": "ContactPoint",
    email: "hi@dotconverse.com",
    contactType: "sales",
    areaServed: "Worldwide",
  },
};
